import { useEffect, useRef, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useClosingAlerts — opens a websocket to /api/ws/closing-alerts and maintains
 * a live list of games closing soon. Client-side ticks every second based on
 * the last server snapshot to smooth UI updates between the 30s server pushes.
 *
 * Returns: { alerts, serverTime, connected } where alerts is an array of
 * { game_id, game_name, closing_time, closes_at, seconds_left }.
 */
export function useClosingAlerts() {
  const [snapshot, setSnapshot] = useState({ alerts: [], server_time: null });
  const [connected, setConnected] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const wsRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      const token = localStorage.getItem("sally_access_token");
      if (!token) {
        retryRef.current = setTimeout(open, 2000);
        return;
      }
      const base = BACKEND_URL.replace(/^http/, "ws");
      const url = `${base}/api/ws/closing-alerts?token=${encodeURIComponent(token)}`;
      let ws;
      try { ws = new WebSocket(url); } catch { retryRef.current = setTimeout(open, 5000); return; }
      wsRef.current = ws;

      ws.onopen = () => { if (!cancelled) setConnected(true); };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data && Array.isArray(data.alerts)) {
            setSnapshot(data);
          }
        } catch {}
      };
      ws.onclose = () => {
        if (cancelled) return;
        setConnected(false);
        retryRef.current = setTimeout(open, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    open();

    const tick = setInterval(() => setNow(Date.now()), 1000);

    return () => {
      cancelled = true;
      clearInterval(tick);
      if (retryRef.current) clearTimeout(retryRef.current);
      if (wsRef.current) { try { wsRef.current.close(); } catch {} }
    };
  }, []);

  // Derive live seconds_left from the last snapshot + client ticks
  const alerts = snapshot.alerts
    .map((a) => {
      const closesAt = new Date(a.closes_at).getTime();
      const secondsLeft = Math.max(0, Math.floor((closesAt - now) / 1000));
      return { ...a, seconds_left: secondsLeft };
    })
    .filter((a) => a.seconds_left > 0)
    .sort((a, b) => a.seconds_left - b.seconds_left);

  return { alerts, serverTime: snapshot.server_time, connected };
}
